import React, { useEffect, useState, useCallback } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api from "../lib/api";
import { getAdSessionId } from "../lib/adAttribution";

/**
 * Native PayPal button for USD/GBP/EUR wallet top-ups.
 *
 * Why this exists (Mar 2026): Razorpay's mobile checkout kept dropping
 * PayPal for international customers on iOS Safari despite the merchant
 * account being activated and `wallet.paypal:true` in preferences.
 * Rather than fight Razorpay's mobile-UI compression on every deploy,
 * we render PayPal natively via @paypal/react-paypal-js and route USD
 * orders through PayPal REST v2 (bypassing Razorpay entirely for non-INR
 * flows). INR customers still go through Razorpay.
 *
 * Flow:
 *   1. On mount → GET /api/paypal/config to fetch client_id + mode.
 *   2. Render PayPalScriptProvider with the fetched client_id.
 *   3. `createOrder` → POST /api/paypal/order → backend creates a
 *      PayPal order via REST v2 and persists a `wallet_orders` row.
 *      Returns the PayPal order id.
 *   4. Buyer approves inside PayPal's popup.
 *   5. `onApprove` → POST /api/paypal/capture → backend captures the
 *      payment and credits the wallet using the same idempotent worker
 *      that Razorpay reconcile uses.
 *
 * Props:
 *   amount       — INR amount to credit (backend converts USD → INR at
 *                  capture time using the FX rate stored on the order).
 *   couponCode   — optional coupon (same validation as Razorpay path).
 *   onSuccess    — fired after wallet credit lands.
 *   onError      — fired when PayPal / backend errors out.
 *   disabled     — true when parent form is invalid.
 */
export default function PayPalTopUpButton({
  amount,
  couponCode,
  onSuccess,
  onError,
  disabled = false,
}) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/paypal/config");
        if (cancelled) return;
        setConfig(data);
      } catch {
        if (!cancelled) setConfig({ client_id: "", mode: "unset" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const createOrder = useCallback(async () => {
    try {
      const { data } = await api.post("/paypal/order", {
        amount: Number(amount) || 0,
        coupon_code: couponCode || null,
      });
      if (!data.paypal_order_id) throw new Error("Missing PayPal order id");
      return data.paypal_order_id;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not create PayPal order.";
      toast.error(msg);
      onError?.(err);
      throw err;
    }
  }, [amount, couponCode, onError]);

  const onApprove = useCallback(async (data) => {
    try {
      const { data: result } = await api.post("/paypal/capture", {
        paypal_order_id: data.orderID,
        ad_session_id: getAdSessionId(),
      });
      if (result.already_credited) {
        toast.success(`PayPal payment already credited. Wallet: ₹${result.balance}`);
      } else {
        const parts = [`₹${result.amount}`];
        if (result.bonus > 0) parts.push(`₹${result.bonus} pack bonus`);
        if (result.coupon_bonus > 0) parts.push(`₹${result.coupon_bonus} (${result.coupon_code})`);
        if (result.cashback_bonus > 0) parts.push(`₹${result.cashback_bonus} cashback 🎁`);
        toast.success(`Wallet topped up: ${parts.join(" + ")} = ₹${result.credited}. Balance: ₹${result.balance}`);
        // Fire GA4 conversion event so PayPal top-ups also count in Ads.
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "conversion_event_purchase", {
            value: Number(result.charge_amount) || Number(result.amount) || 0,
            currency: result.charge_currency || "USD",
            transaction_id: result.order_id || data.orderID,
            new_customer: !!result.is_new_customer,
          });
        }
      }
      onSuccess?.(result);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Payment capture failed.";
      toast.error(msg);
      onError?.(err);
    }
  }, [onSuccess, onError]);

  if (loading) {
    return (
      <div
        className="mt-3 h-11 rounded-md bg-slate-100 animate-pulse"
        data-testid="paypal-btn-loading"
      />
    );
  }

  if (!config?.client_id) {
    // PayPal not configured — silently hide (Razorpay button below still works).
    return null;
  }

  return (
    <div className="mt-3" data-testid="paypal-btn-wrap">
      <PayPalScriptProvider
        options={{
          clientId: config.client_id,
          currency: "USD",
          intent: "capture",
          // Enable venmo + pay-in-4 for eligible US buyers (extra revenue).
          "enable-funding": "venmo,paylater",
          "disable-funding": "credit",
          components: "buttons",
        }}
      >
        <PayPalButtons
          style={{
            layout: "horizontal",
            color: "gold",
            shape: "pill",
            label: "paypal",
            height: 44,
            tagline: false,
          }}
          disabled={disabled || !amount || Number(amount) <= 0}
          forceReRender={[amount, couponCode]}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={(err) => {
            console.error("[PayPal] SDK error:", err);
            toast.error("PayPal error. Please try again or use another payment method.");
            onError?.(err);
          }}
          onCancel={() => {
            toast.info("PayPal payment cancelled.");
          }}
        />
      </PayPalScriptProvider>
      <p className="text-[10px] text-muted text-center mt-2">
        Pay in USD via PayPal (Venmo &amp; Pay-in-4 supported for US buyers).
        Wallet credited in ₹ using today&apos;s FX rate.
      </p>
    </div>
  );
}
